import { createContext, useContext } from 'react';
import { Linkable } from '@/components/blocks/Linkable';
import { useIsOpen } from 'integration/hooks/useIsOpen';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { combineSX } from '@/utils/combineSX';
import { Stack, SxProps, Typography } from '@mui/material';

const DpDisclaimerSizeHandlerContext = createContext<ReturnType<typeof useIsOpen>>({
	open: false,
	on: () => null,
	off: () => null,
	toggle: () => null,
});

const DpDisclaimerSizeHandlerProvider = DpDisclaimerSizeHandlerContext.Provider;

export const useDpDisclaimerSizeHandler = () => {
	const context = useContext(DpDisclaimerSizeHandlerContext);

	if (!context) {
		throw new Error(
			'useDpDisclaimerSizeHandler must be used within a DpDisclaimerSizeHandlerProvider'
		);
	}

	return context;
};

export const DpDisclaimerSizeHandlerOpen: React.FC<React.PropsWithChildren<{ sx?: SxProps }>> = ({
	children,
	sx,
	...props
}) => {
	const { on } = useDpDisclaimerSizeHandler();

	return (
		<Linkable
			href=""
			type="inline"
			sx={combineSX([
				{
					fontSize: 'var(--font-size-small)',
					textDecoration: 'none',
					margin: '0 1px 0 3px',
					fontWeight: 'normal',
					position: 'relative',
					bottom: '1px',
					'&:hover': {
						textDecoration: 'none',
					},
				},
				...(Array.isArray(sx) ? sx : [sx]),
			])}
			onClick={on}
			{...props}
		>
			{children}
		</Linkable>
	);
};

export const DpDisclaimerSizeIcon: React.FC<{ sx?: SxProps }> = ({ sx, ...props }) => (
	<InfoOutlinedIcon
		sx={combineSX([
			{
				color: '#565656',
				fontSize: 'var(--font-size-caption)',
				marginRight: '2px',
				marginTop: '4px',
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		{...props}
	/>
);

export const DpDisclaimerSizeHandler: React.FC<React.PropsWithChildren> = ({ children }) => {
	const openDrawer = useIsOpen();

	return (
		<DpDisclaimerSizeHandlerProvider value={openDrawer}>{children}</DpDisclaimerSizeHandlerProvider>
	);
};

export const DpDisclaimerSizeFrame: React.FC<
	React.PropsWithChildren<{
		sx?: SxProps;
	}>
> = ({ children, sx, ...props }) => (
	<Stack
		sx={combineSX([
			{
				marginTop: '7.72px !important',
				marginBottom: { xs: '0px !important', sm: '0px !important' },
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		direction="row"
		{...props}
	>
		{children}
	</Stack>
);

export const DpDisclaimerSizeText: React.FC<
	React.PropsWithChildren<{
		sx?: SxProps;
	}>
> = ({ children, sx, ...props }) => (
	<Typography
		sx={combineSX([
			{
				// wordBreak: 'break-word',
				display: 'inline',
				fontSize: 'var(--font-size-small)',
				letterSpacing: '0px',
				color: '#2C2C2C',
				fontWeight: 'normal',
				opacity: 1,
				marginLeft: '3px',
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		{...props}
	>
		{children}
	</Typography>
);

export const DpDisclaimerSizeTextAccent: React.FC<
	React.PropsWithChildren<{
		sx?: SxProps;
	}>
> = ({ children, sx, ...props }) => (
	<Typography
		sx={combineSX([
			{
				display: 'inline',
				alignItems: 'center',
				color: '#2C2C2C',
				marginTop: '0rem',
				fontWeight: 'bold',
				letterSpacing: '0',
				fontSize: 'var(--font-size-small)',
			},
			...(Array.isArray(sx) ? sx : [sx]),
		])}
		{...props}
	>
		{children}
	</Typography>
);
